/**
 * Possible weight units
 */
export enum WeightUnit {
  Kilograms = 'KILOGRAMS',
  Pounds = 'POUNDS',
  PoundsStones = 'POUNDS_STONES',
}

/**
 * Possible height units
 */
export enum HeightUnit {
  Centimetres = 'CENTIMETRES',
  Inches = 'INCHES',
}

/**
 * Possible amh units
 */
export enum AmhUnit {
  PicomolesPerLitre = 'PICOMOLES_PER_LITRE',
  NanogramsPerMilliliter = 'NANOGRAMS_PER_MILLILITER',
}

export type Weight = {
  unit: WeightUnit
  value: number
}

export type Height = {
  unit: HeightUnit
  value: number
}

/**
 * Convert kg to lb
 */
export const kgToLb = (kg: number) => {
  return Math.round(kg / 0.453592)
}

/**
 * Convert stones and pounds to total pounds
 */
export const stLbToLb = (st: number, lb: number) => {
  return Math.round(lb + st * 14)
}

/**
 * Convert pounds to multiple of stones and remainder pounds
 */
export const lbToStLb = (lb: number) => {
  return [Math.floor(lb / 14), Math.round(lb % 14)]
}

/**
 * Convert pounds to kg
 */
export const lbToKg = (lb: number) => {
  return Math.round(lb * 0.453592)
}

/**
 * Convert cm to inches
 */
export const cmToInches = (cm: number) => {
  return Math.round(cm * 0.3937)
}

/**
 * Convert inches into cm
 */
export const inchesToCm = (inches: number) => {
  return Math.round(inches * 2.54)
}

/**
 * Split inches into feet with remainder inches
 */
export const inchesToFtIn = (inches: number) => {
  return [Math.floor(inches / 12), Math.round(inches % 12)]
}

/**
 * Sum ft and inches input to total inches
 */
export const ftInToInches = (ft: number, inches: number) => {
  return (ft ?? 0) * 12 + (inches ?? 0)
}

/**
 * Calculate a bmi from height and weight
 *
 * @see https://www.nhs.uk/live-well/healthy-weight/bmi-calculator/
 */
export const bmi = (height: Gaia.Height, weight: Gaia.Weight) => {
  const normalWeight =
    weight.unit === WeightUnit.Kilograms ? weight.value : lbToKg(weight.value)

  const normalHeight =
    height.unit === HeightUnit.Centimetres
      ? height.value
      : inchesToCm(height.value)

  return normalWeight / (normalHeight / 100) ** 2
}

export const isBmiWithinRange = (
  height: Gaia.Height | null | undefined,
  weight: Gaia.Weight | null | undefined
) => {
  const errorMaxBmi = 70
  const errorMinBmi = 10

  return !!(
    !height?.value ||
    !weight?.value ||
    (bmi(height, weight) <= errorMaxBmi && bmi(height, weight) >= errorMinBmi)
  )
}

const RATIO_PMOLL_PER_NGML = 7.18

/**
 * Convert from Nanograms Per Milliliter
 */
export const ngmlToPmoll = (ngml: number | null = null) => {
  if (ngml === null) {
    return null
  }
  return Math.round(ngml * RATIO_PMOLL_PER_NGML * 100) / 100
}

/**
 * Convert to Nanograms Per Milliliter
 */
export const pmollToNgml = (pmoll: number | null = null) => {
  if (pmoll === null) {
    return null
  }
  return Math.round((pmoll / RATIO_PMOLL_PER_NGML) * 100) / 100
}
